import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { ScorecardRow } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  EmptyRow, ExportMenu, FilterChip, SortableHeader, StatusPill, downloadCsv, trim,
} from '../components/ui';

type SortColumn = 'rank' | 'name' | 'department' | 'locationCode' | 'deductions' | 'score' | 'status';

/** The flat, sortable roster. Default sort is worst-first, because that is
 *  what a manager opens this screen to find. */
export function ScorecardSummary() {
  const { reference, periodId, setPeriodId, bandColor, bands } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const maxScore = params.get('maxScore');
  const locationParam = params.get('location') ?? 'all';

  const [department, setDepartment] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ column: SortColumn; dir: 'asc' | 'desc' }>({ column: 'score', dir: 'asc' });

  const { data, isLoading } = useQuery({
    queryKey: ['scorecard', periodId, locationParam, department, status, search, maxScore],
    queryFn: () =>
      api.get<{ rows: ScorecardRow[]; totalCount: number; rankedCount: number }>('/api/scorecard', {
        periodId, location: locationParam, department, status, search, maxScore,
      }),
  });

  const rows = useMemo(() => {
    const list = [...(data?.rows ?? [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.column) {
        case 'name': return a.name.localeCompare(b.name) * dir;
        case 'department': return (a.department ?? '').localeCompare(b.department ?? '') * dir;
        case 'locationCode': return (a.locationCode ?? '').localeCompare(b.locationCode ?? '') * dir;
        case 'deductions': return (a.deductions - b.deductions) * dir;
        case 'status': return a.status.localeCompare(b.status) * dir;
        case 'rank': return (a.rank - b.rank) * dir;
        default: return (a.score - b.score) * dir;
      }
    });
    return list;
  }, [data, sort]);

  const toggleSort = (column: string) =>
    setSort((s) => ({ column: column as SortColumn, dir: s.column === column && s.dir === 'asc' ? 'desc' : 'asc' }));

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Scorecard Summary</div>
          <div className="page-subtitle">
            Every employee · {reference.periods.find((p) => p.periodId === periodId)?.name} — every score is the sum of
            approved events dated inside the period
          </div>
        </div>
        <ExportMenu
          onExport={() =>
            downloadCsv(
              'scorecard-summary.csv',
              ['Rank (1 = best)', 'Employee ID', 'Employee', 'Department', 'Location', 'Deductions', 'Q1', 'Q2', 'Q3', 'Q4', 'Score', 'Status'],
              rows.map((r) => [
                r.rank, r.employeeId, r.name, r.department, r.locationCode, r.deductions,
                ...r.quarterRewards, r.score, r.status,
              ]),
            )
          }
        />
      </div>

      <div className="filter-row">
        <select className="control" value={department} onChange={(e) => setDepartment(e.target.value)} aria-label="Department">
          <option value="all">All departments</option>
          {reference.departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="control" value={periodId} onChange={(e) => setPeriodId(Number(e.target.value))} aria-label="Period">
          {reference.periods.map((p) => (
            <option key={p.periodId} value={p.periodId}>
              {p.isCurrent ? 'Current' : p.status === 'locked' ? 'Closed' : 'Upcoming'} — {p.name}
            </option>
          ))}
        </select>
        <select className="control" value={locationParam} onChange={(e) => setParam('location', e.target.value)} aria-label="Location">
          <option value="all">All locations</option>
          {reference.facilities.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
        </select>
        <select className="control" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="all">All statuses</option>
          {bands.map((b) => <option key={b.label} value={b.label}>{b.label}</option>)}
        </select>
        <input className="control" style={{ width: 200 }} placeholder="Search by name or employee ID…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div className="meta">{rows.length} of {data?.totalCount ?? 0} employees</div>
        {/* Arriving from the dashboard's Below Good Standing card, the filter
            that made this list short is visible and clearable in one click. */}
        {maxScore && <FilterChip label={`Score below ${maxScore}`} onClear={() => setParam('maxScore', null)} />}
        {locationParam !== 'all' && (
          <FilterChip
            label={reference.facilities.find((f) => f.code === locationParam)?.name ?? locationParam}
            onClear={() => setParam('location', null)}
          />
        )}
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="RANK" column="rank" sort={sort} onSort={toggleSort} className="num" />
              <th className="num">ID</th>
              <SortableHeader label="EMPLOYEE" column="name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="DEPARTMENT" column="department" sort={sort} onSort={toggleSort} />
              <SortableHeader label="LOCATION" column="locationCode" sort={sort} onSort={toggleSort} />
              <SortableHeader label="DEDUCT." column="deductions" sort={sort} onSort={toggleSort} className="num" />
              <th className="num">Q1</th>
              <th className="num">Q2</th>
              <th className="num">Q3</th>
              <th className="num">Q4</th>
              <SortableHeader label="SCORE" column="score" sort={sort} onSort={toggleSort} className="num" />
              <SortableHeader label="STATUS" column="status" sort={sort} onSort={toggleSort} />
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && <EmptyRow colSpan={13}>Computing scores for this period…</EmptyRow>}
            {!isLoading && rows.length === 0 && (
              <EmptyRow colSpan={13}>No employees match these filters.</EmptyRow>
            )}
            {rows.map((r) => {
              const band = bandColor(r.status);
              return (
                <tr key={r.employeeId} className="clickable" onClick={() => navigate(`/employee/${r.employeeId}`)}>
                  <td className="num" style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {r.rank ? `${r.rank} of ${data?.rankedCount ?? '—'}` : '—'}
                  </td>
                  <td className="num">{r.employeeId}</td>
                  <td className="strong">{r.name}</td>
                  <td>{r.department ?? '—'}</td>
                  <td>{r.locationCode ?? '—'}</td>
                  <td className="num">{r.deductions === 0 ? '—' : `-${trim(r.deductions)}`}</td>
                  {r.quarterRewards.map((q, i) => (
                    <td key={i} className="num">{q === 0 ? '—' : `+${trim(q)}`}</td>
                  ))}
                  <td className="num" style={{ fontSize: 13.5, fontWeight: 700, color: band.color }}>{trim(r.score)}</td>
                  <td><StatusPill label={r.status} color={band.color} bg={band.bg} /></td>
                  <td className="num" style={{ color: 'var(--blue)', fontWeight: 600, whiteSpace: 'nowrap' }}>View →</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
